import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
import { BranchFilter } from "@/components/BranchFilter";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { Plus, X, Trash2, ArrowRightLeft, CheckCircle2, Truck, Info, Search } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { formatUser, useUserDirectory } from "@/components/UserLabel";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";

const STATUS_TABS = [
  { k: "All", label: "All" },
  { k: "Draft", label: "Draft" },
  { k: "Approved", label: "In-Transit" },
  { k: "Received", label: "Received" },
  { k: "Cancelled", label: "Cancelled" },
];

export default function StockTransferPage() {
  useUserDirectory();
  const navigate = useNavigate();
  const [filterBranch, setFilterBranch] = useState("");
  const [statusTab, setStatusTab] = useState("All");
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);

  const params = useMemo(() => (filterBranch ? { branch_id: filterBranch } : {}), [filterBranch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/stock-transfers", params);
  const load = reload;
  const openFormTab = useFormTab();

  const openNew = () => openFormTab({
    path: "/stock-transfers/new",
    label: "New Stock Transfer",
    icon: Plus,
    parent: "/stock-transfers",
    onSaved: load,
    render: ({ close, saved, viewList }) => <NewSTForm onClose={close} onViewList={viewList} onSaved={saved} />,
  });

  // Auto-spawn "New Stock Transfer" tab on landing.
  const autoSpawnedRef = useRef(false);
  useEffect(() => {
    if (autoSpawnedRef.current) return;
    autoSpawnedRef.current = true;
    openNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToDashboard = () => navigate("/");

  const filtered = useMemo(() => {
    let base = statusTab === "All" ? list : list.filter(t => t.status === statusTab);
    const q = search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(t => {
      const lineHit = (t.line_items || []).some(li =>
        ((li.item_name || "") + " " + (li.item_code || "") + " " + (li.batch_code || "")).toLowerCase().includes(q));
      return lineHit || [
        t.transfer_number, t.from_branch_name, t.to_branch_name, t.vehicle_no, t.driver_name,
        t.lr_no, t.notes, t.status, (t.transfer_date || "").slice(0, 10),
      ].some(x => (x || "").toString().toLowerCase().includes(q));
    });
  }, [list, statusTab, search]);

  const counts = useMemo(() => {
    const m = { All: list.length, Draft: 0, Approved: 0, Received: 0, Cancelled: 0 };
    list.forEach(t => { m[t.status] = (m[t.status] || 0) + 1; });
    return m;
  }, [list]);

  // Stock-Transfer rows can be Draft (waiting for L1 approval) or "Pending"
  // for matrix-based modules. Map "Draft" → Pending lookup as well, since the
  // approval engine creates an entry the moment the doc is submitted.
  const pendingIds = useMemo(() => (list || []).filter(t => t && (t.status === "Pending" || t.status === "Draft")).map(t => t.id).filter(Boolean), [list]);
  const pendingMap = useApprovalPendingMap("stock_transfer", pendingIds);

  async function approve(id) {
    try { await api.post(`/stock-transfers/${id}/approve`); toast.success("Approved · In-Transit"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function receive(id) {
    try { await api.post(`/stock-transfers/${id}/receive`); toast.success("Received at destination"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function cancel(id) {
    if (!confirm("Cancel this transfer? Reserved batches will be released.")) return;
    try { await api.post(`/stock-transfers/${id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Stores"
        title="Inter-Branch Stock Transfer"
        subtitle="Move FIFO batches between branches — Draft → Approved (In-Transit) → Received."
        actions={
          <button data-testid="new-st-btn" onClick={openNew}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> New Transfer
          </button>
        }
      />

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="st-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by transfer / branch / vehicle / item / batch…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[340px]" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Branch</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="st-branch-filter" />
        <div className="ml-auto text-xs text-slate-500">
          <span className="text-slate-400 mr-1">Showing:</span>
          <span className="font-bold text-slate-800 tabular">{filtered.length}</span>
          <span className="text-slate-400 mx-1">/</span>
          <span className="text-slate-600 tabular">{list.length}</span>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="st-status-tabs">
        {STATUS_TABS.map(s => (
          <button key={s.k} data-testid={`st-tab-${s.k.toLowerCase()}`} onClick={() => setStatusTab(s.k)}
            className={`px-4 py-2.5 text-[11px] uppercase tracking-wider font-bold border-b-2 -mb-px ${statusTab === s.k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {s.label} <span className="ml-1 text-slate-400">({counts[s.k] || 0})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No stock transfers in this view" hint="Click ‘New Transfer’ to create one." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="st-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Transfer No", "Date", "From", "To", "Lines", "Value", "Vehicle / LR", "Status", ""].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-3 px-4 text-xs">
                      <button data-testid={`view-st-${t.id}`} onClick={() => setViewing(t)}
                        className="font-mono font-bold text-[#F97316] hover:underline">{t.transfer_number}</button>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">{(t.transfer_date || "").slice(0, 10)}</td>
                    <td className="py-3 px-4 text-xs text-slate-700">{t.from_branch_name}</td>
                    <td className="py-3 px-4 text-xs text-slate-700">
                      <ArrowRightLeft size={11} className="inline mr-1 text-slate-400" />{t.to_branch_name}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">{(t.line_items || []).length}</td>
                    <td className="py-3 px-4 text-sm tabular font-bold text-[#F97316]">{formatINR(t.total_value || 0)}</td>
                    <td className="py-3 px-4 text-[11px] text-slate-500">
                      {t.vehicle_no || "—"}{t.lr_no ? ` · LR ${t.lr_no}` : ""}
                    </td>
                    <td className="py-3 px-4"><PendingApprovalBadge status={t.status === "Approved" ? "Approved" : t.status} pendingInfo={pendingMap[t.id]} /></td>
                    <td className="py-3 px-4 text-xs whitespace-nowrap">
                      {pendingMap[t.id] && (
                        <span className="mr-2 inline-block align-middle">
                          <TakeActionButton pendingInfo={pendingMap[t.id]} testid={`take-action-st-${t.id}`} />
                        </span>
                      )}
                      {t.status === "Draft" &&
                        <button data-testid={`st-approve-${t.id}`} onClick={() => approve(t.id)}
                          className="text-emerald-700 hover:underline mr-3">Approve</button>}
                      {t.status === "Approved" &&
                        <button data-testid={`st-receive-${t.id}`} onClick={() => receive(t.id)}
                          className="text-[#F97316] hover:underline mr-3 inline-flex items-center gap-1">
                          <CheckCircle2 size={11} />Receive</button>}
                      {(t.status === "Draft" || t.status === "Approved") &&
                        <button data-testid={`st-cancel-${t.id}`} onClick={() => cancel(t.id)}
                          className="text-red-600 hover:underline">Cancel</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
      )}

      {viewing && <STView t={viewing} onViewList={() => setViewing(null)}
        onClose={() => { setViewing(null); navigate("/"); }}
        onAction={() => { setViewing(null); load(); }} />}
    </div>
  );
}

function NewSTForm({ onClose, onViewList, onSaved }) {
  const mastersRefreshKey = useMastersRefresh();
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    transfer_date: today, from_branch_id: "", to_branch_id: "",
    vehicle_no: "", driver_name: "", lr_no: "", notes: "",
    line_items: [],
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/branches").then(r => setBranches(r.data)); }, [mastersRefreshKey]);
  useEffect(() => {
    if (form.from_branch_id) {
      api.get(`/inventory/batches?branch_id=${form.from_branch_id}&include_depleted=false`).then(r => setBatches(r.data));
    } else { setBatches([]); }
  }, [form.from_branch_id]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  function addLine() {
    setForm(p => ({ ...p, line_items: [...p.line_items, { batch_id: "", item_id: "", item_code: "", item_name: "", batch_code: "", unit: "", qty: 0, unit_rate: 0, remaining_qty: 0 }] }));
  }
  function removeLine(i) { setForm(p => ({ ...p, line_items: p.line_items.filter((_, j) => j !== i) })); }
  function updLine(i, patch) { setForm(p => ({ ...p, line_items: p.line_items.map((l, j) => j === i ? { ...l, ...patch } : l) })); }

  const [searchTxt, setSearchTxt] = useState({});

  function pickBatch(i, bid) {
    const b = batches.find(x => x.id === bid);
    if (!b) return;
    updLine(i, {
      batch_id: b.id, batch_code: b.batch_code,
      item_id: b.item_id, item_code: b.item_code, item_name: b.item_name,
      unit: b.unit, unit_rate: b.unit_rate, remaining_qty: b.remaining_qty,
    });
  }

  const batchLabel = (b) => `${b.item_name} · ${b.batch_code} · ${b.remaining_qty} ${b.unit} · ${b.item_code}`;

  const totalValue = useMemo(
    () => form.line_items.reduce((s, li) => s + Number(li.qty || 0) * Number(li.unit_rate || 0), 0),
    [form.line_items]
  );

  async function save(e) {
    e.preventDefault();
    if (!form.from_branch_id || !form.to_branch_id) { toast.error("Pick both branches"); return; }
    if (form.from_branch_id === form.to_branch_id) { toast.error("From and To branches must differ"); return; }
    if (form.line_items.length === 0) { toast.error("Add at least one line"); return; }
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (!li.batch_id) { toast.error(`Line ${i + 1}: pick a batch`); return; }
      if (Number(li.qty) <= 0) { toast.error(`Line ${i + 1}: qty must be > 0`); return; }
      if (Number(li.qty) > Number(li.remaining_qty)) {
        toast.error(`Line ${i + 1}: qty ${li.qty} exceeds available ${li.remaining_qty} ${li.unit}`); return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form, total_value: totalValue };
      await api.post("/stock-transfers", payload);
      toast.success("Transfer created (Draft)");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="st-form-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
        <div>
          <h2 className="font-display text-2xl font-bold">New Stock Transfer</h2>
          <p className="text-xs text-slate-500 mt-0.5">FIFO batches move between branches · Draft holds the batch; Approved decrements source; Received credits destination.</p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" data-testid="st-view-list-btn" onClick={onViewList}
            className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">View List</button>
          <RefreshMastersButton testid="st-refresh-masters-btn" />
          <button type="button" data-testid="st-close-btn" onClick={onClose}
            className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
        </div>
      </div>

      <form onSubmit={save} className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Field label="Transfer No (auto · admin can override)">
            <VoucherNumberInput
              module="stock_transfer"
              value={form.transfer_number || ""}
              onChange={v => setF("transfer_number", v)}
              testidPrefix="st-number"
            />
          </Field>
          <Field label="Transfer Date *">
            <input data-testid="st-date" type="date" required value={form.transfer_date}
              onChange={e => setF("transfer_date", e.target.value)} className={inputCls} />
          </Field>
          <Field label="From Branch *">
            <select data-testid="st-from" required value={form.from_branch_id}
              onChange={e => { setF("from_branch_id", e.target.value); setF("line_items", []); }}
              className={inputCls}>
              <option value="">Select…</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
            </select>
          </Field>
          <Field label="To Branch *">
            <select data-testid="st-to" required value={form.to_branch_id}
              onChange={e => setF("to_branch_id", e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {branches.filter(b => b.id !== form.from_branch_id).map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
            </select>
          </Field>
          <Field label="Reference / Notes">
            <input data-testid="st-notes" value={form.notes} onChange={e => setF("notes", e.target.value)} className={inputCls} placeholder="Optional remarks" />
          </Field>
        </div>

        <details className="border border-slate-200 bg-slate-50/40 px-4 py-3" data-testid="st-transport-section">
          <summary className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 cursor-pointer select-none">
            Transportation Details
            {(form.vehicle_no || form.driver_name || form.lr_no) && (
              <span className="ml-2 text-[#F97316] normal-case">· populated</span>
            )}
          </summary>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <Field label="Vehicle No"><input data-testid="st-vehicle" value={form.vehicle_no} onChange={e => setF("vehicle_no", e.target.value)} className={inputCls} placeholder="e.g. WB-12-AB-1234" /></Field>
            <Field label="Driver"><input data-testid="st-driver" value={form.driver_name} onChange={e => setF("driver_name", e.target.value)} className={inputCls} placeholder="Name / contact" /></Field>
            <Field label="LR / Docket No"><input data-testid="st-lr" value={form.lr_no} onChange={e => setF("lr_no", e.target.value)} className={inputCls} /></Field>
          </div>
        </details>

        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Line Items</h3>
            <button type="button" data-testid="st-add-line" onClick={addLine} disabled={!form.from_branch_id}
              className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
              <Plus size={11} /> Add Line
            </button>
          </div>
          {!form.from_branch_id && (
            <p className="text-xs text-slate-500 italic flex items-center gap-1"><Info size={12} /> Pick a source branch to load available FIFO batches.</p>
          )}
          {form.from_branch_id && batches.length === 0 && (
            <p className="text-xs text-amber-700 italic flex items-center gap-1"><Info size={12} /> No active batches at the source branch.</p>
          )}

          {form.line_items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="st-lines-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["#", "Batch / Item", "Available", "Qty", "Rate", "Line Value", ""].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {form.line_items.map((li, i) => (
                    <tr key={i} className="border-b border-slate-100">
                      <td className="py-2 px-2 text-xs text-slate-500">{i + 1}</td>
                      <td className="py-2 px-2">
                        <input data-testid={`st-batch-${i}`} type="text"
                          list={`st-batches-list-${i}`}
                          value={searchTxt[i] !== undefined ? searchTxt[i] : (li.batch_id ? batchLabel(batches.find(b => b.id === li.batch_id) || { item_name: li.item_name || "", batch_code: li.batch_code || "", remaining_qty: li.remaining_qty || 0, unit: li.unit || "", item_code: li.item_code || "" }) : "")}
                          onChange={e => {
                            const v = e.target.value;
                            setSearchTxt(prev => ({ ...prev, [i]: v }));
                            const match = batches.find(b => batchLabel(b).toLowerCase() === (v || "").trim().toLowerCase());
                            if (match) {
                              pickBatch(i, match.id);
                              setSearchTxt(prev => { const n = { ...prev }; delete n[i]; return n; });
                            }
                          }}
                          onFocus={() => setSearchTxt(prev => ({ ...prev, [i]: "" }))}
                          onBlur={() => setSearchTxt(prev => { const n = { ...prev }; delete n[i]; return n; })}
                          className={inputCls}
                          placeholder="Search by item, batch, code…" />
                        <datalist id={`st-batches-list-${i}`}>
                          {batches.map(b => <option key={b.id} value={batchLabel(b)} />)}
                        </datalist>
                      </td>
                      <td className="py-2 px-2 text-xs text-slate-600 tabular">
                        {li.remaining_qty || 0} {li.unit}
                      </td>
                      <td className="py-2 px-2">
                        <input data-testid={`st-qty-${i}`} type="number" step="0.01" value={li.qty}
                          onChange={e => updLine(i, { qty: Number(e.target.value) })}
                          className={`${inputCls} w-28`} />
                      </td>
                      <td className="py-2 px-2 text-xs tabular text-slate-700">{formatINR(li.unit_rate || 0)}</td>
                      <td className="py-2 px-2 text-sm tabular font-bold text-[#F97316]">
                        {formatINR(Number(li.qty || 0) * Number(li.unit_rate || 0))}
                      </td>
                      <td className="py-2 px-2">
                        <button type="button" onClick={() => removeLine(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50">
                    <td colSpan={5} className="py-2 px-2 text-right text-xs uppercase font-bold text-slate-500">Total</td>
                    <td className="py-2 px-2 text-sm tabular font-bold text-[#F97316]" data-testid="st-total">{formatINR(totalValue)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
          <button type="button" onClick={onClose}
            className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="save-st-btn" disabled={saving} type="submit"
            className="px-6 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-bold uppercase disabled:opacity-50">
            {saving ? "Saving…" : "Save (Draft)"}
          </button>
        </div>
      </form>
    </div>
  );
}

function STView({ t, onViewList, onClose, onAction }) {
  async function approve() { try { await api.post(`/stock-transfers/${t.id}/approve`); toast.success("Approved · In-Transit"); onAction(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }
  async function receive() { try { await api.post(`/stock-transfers/${t.id}/receive`); toast.success("Received"); onAction(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }
  async function cancel() { if (!confirm("Cancel this transfer?")) return; try { await api.post(`/stock-transfers/${t.id}/cancel`); toast.success("Cancelled"); onAction(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } }

  return (
    <ViewOverlay testid="st-view-overlay">
      <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Stock Transfer</div>
          <h2 className="font-display text-2xl font-bold font-mono text-[#F97316]">{t.transfer_number}</h2>
        </div>
        <div className="flex items-center gap-2">
          {t.status === "Draft" && <button data-testid="stv-approve-btn" onClick={approve} className="bg-emerald-600 text-white px-4 py-2 text-xs uppercase font-bold">Approve · In-Transit</button>}
          {t.status === "Approved" && <button data-testid="stv-receive-btn" onClick={receive} className="bg-[#F97316] text-white px-4 py-2 text-xs uppercase font-bold">Mark Received</button>}
          {(t.status === "Draft" || t.status === "Approved") && <button data-testid="stv-cancel-btn" onClick={cancel} className="border border-red-300 text-red-700 px-4 py-2 text-xs uppercase font-bold hover:bg-red-50">Cancel</button>}
          <button type="button" onClick={onViewList} className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">View List</button>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
        </div>
      </div>

      <div className="px-8 py-6 max-w-[1400px] mx-auto space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <Meta k="Status" v={<StatusBadge status={t.status} />} />
          <Meta k="Date" v={(t.transfer_date || "").slice(0, 10)} />
          <Meta k="From" v={t.from_branch_name} />
          <Meta k="To" v={t.to_branch_name} />
          <Meta k="Vehicle" v={t.vehicle_no || "—"} />
          <Meta k="Driver" v={t.driver_name || "—"} />
          <Meta k="LR No" v={t.lr_no || "—"} />
          <Meta k="Received" v={t.received_date || "—"} />
          <Meta k="Created By" v={formatUser(t.created_by, "—")} />
        </div>
        {t.notes && <div className="text-xs text-slate-600 border-l-2 border-slate-300 pl-3">{t.notes}</div>}

        <div>
          <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-2">Lines</h3>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>{["Item", "Batch", "Qty", "Unit Rate", "Line Value"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {(t.line_items || []).map((li, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-2 px-3 text-sm">{li.item_name}<div className="text-[10px] text-slate-400">{li.item_code}</div></td>
                    <td className="py-2 px-3 text-xs font-mono">{li.batch_code}</td>
                    <td className="py-2 px-3 text-sm tabular">{li.qty} {li.unit}</td>
                    <td className="py-2 px-3 text-sm tabular">{formatINR(li.unit_rate || 0)}</td>
                    <td className="py-2 px-3 text-sm tabular font-bold">{formatINR(Number(li.qty || 0) * Number(li.unit_rate || 0))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="bg-slate-50"><td colSpan={4} className="py-2 px-3 text-right text-xs uppercase font-bold text-slate-500">Total</td>
                <td className="py-2 px-3 text-sm tabular font-bold text-[#F97316]">{formatINR(t.total_value || 0)}</td></tr></tfoot>
            </table>
          </Card>
        </div>
      </div>
    </ViewOverlay>
  );
}

function Meta({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{k}</div>
      <div className="text-sm text-slate-800 mt-0.5">{v}</div>
    </div>
  );
}
